/* eslint-disable no-underscore-dangle */
import { ILogger } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ISlideCountState } from '@src/slide/domain/slide-app/store/slide-count-state';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { EditEvents, IEditEvents } from '../../../core/interfaces/event.interface';
import { IClientVarsService } from '../../../core/interfaces/client-vars-service.interface';

enum CustomLoadPerformancePoint {
    TIME_ORIGIN = 'time_origin',
    PARSE_HTML = 'parse_html',
    MAIN_START = 'main_start',
    CAN_VIEW = 'can_view',
    CAN_INTERACT = 'can_interact',
    CAN_EDIT = 'can_edit',
    ALL_CHUNKS_FETCHED = 'all_chunks_fetched',
}
const DocType = { Slide: 4 };
// 默认非新建
const DefaultDocStatus = 0;

interface ReportBaseOptions {
    /**
     * 文档类型
     * slide = 4
     */
    docType: number;
    /**
     * 文档状态
     * 0（打开）、12（新建） 取自clientVars.docStatus
     */
    docStatus: number;
    /**
     * 文档id
     */
    globalPadId: string;
    /**
     * 性能上报 <PerformancePoint, timestamp>
     * 在已有点位外也可自行扩展
     */
    metrics: Record<string, number>;
    /**
     * 额外附加参数
     */
    extra?: Record<string, any>;
}

interface ReportLoadOptions extends ReportBaseOptions {
    type: 'load';
}


export class OnlineEditReporter {
    private loadReportOptions: ReportLoadOptions;

    public constructor(
        protected logger: ILogger,
        private clientVarsService: IClientVarsService,
        protected slideCountState: ISlideCountState,
        protected commonEmitter: IEditEvents,
    ) {
        const { type = DocType.Slide, docStatus = DefaultDocStatus, globalPadId = '' } = this.clientVarsService.getClientVars();
        this.loadReportOptions = {
            docType: type,
            type: 'load',
            docStatus,
            globalPadId,
            metrics: {},
            extra: {},
        };

        this.reportLoadMetric(CustomLoadPerformancePoint.TIME_ORIGIN, performance?.timeOrigin);
        this.reportLoadMetric(CustomLoadPerformancePoint.PARSE_HTML, (window as any).__webStartParseHtmlTimestamp);
        this.reportLoadMetric(CustomLoadPerformancePoint.MAIN_START, window.jsRunMarkPoints?.mainStart);

        this.commonEmitter.on(EditEvents.LoadDocumentAllChunksSuccess, () => {
            this.reportLoadMetric(CustomLoadPerformancePoint.ALL_CHUNKS_FETCHED);
        });

        eventBus.onIncludingPast(EventKeys.FIRST_PAGE_DISPLAY, () => {
            this.reportLoadMetric(CustomLoadPerformancePoint.CAN_VIEW, (window as any).previewTime);
            this.reportLoadMetric(CustomLoadPerformancePoint.CAN_INTERACT);
            // PC 客户端 可见/可交互/可编辑为同一时机
            if (browser.isPc) {
                this.reportLoadMetric(CustomLoadPerformancePoint.CAN_EDIT);
            }
        });
    }

    private reportLoadMetric(action: string, timestamp = Date.now()) {
        this.logger.info(
            '[OnlineEditReporter]',
            `[${action.toUpperCase()}]`,
            `@${~~(timestamp - performance.timeOrigin)}`,
        );

        const { metrics } = this.loadReportOptions;
        metrics[action] = timestamp;

        /**
         * ALL_CHUNKS_FETCHED 和 FIST_PAGE_DISPLAY 先后顺序不固定
         * 因此在文档收集到所有关键数据时，再上报所有数据
         * 桌面端首屏可见即为 可见/可交互/可编辑 会导致 ALL_CHUNKS_FETCHED/FIST_PAGE_DISPLAY 两次触发上报
         * 所以调整为 ALL_CHUNKS_FETCHED && FIST_PAGE_DISPLAY 同时满足后再一次上报
         */
        if (
            metrics[CustomLoadPerformancePoint.CAN_VIEW]
            && metrics[CustomLoadPerformancePoint.CAN_INTERACT]
            && metrics[CustomLoadPerformancePoint.CAN_EDIT]
            && metrics[CustomLoadPerformancePoint.ALL_CHUNKS_FETCHED]
        ) {
            // 最终上报时再获取 slideCount
            this.loadReportOptions.extra = this.getExtraParams();
            this.report(this.loadReportOptions);
        }
    }

    /**
     * 目前仅桌面客户端需上报
     * @param options
     */
    private report(options: ReportLoadOptions) {
        this.logger.info('[OnlineEditReporter]', options.type, JSON.stringify(options));
        if (!browser.isTencentDocDesktopApp) {
            return;
        }
        window.mqq.invoke('docx', 'performanceReport', options);
    }

    private getExtraParams() {
        return {
            name: this.clientVarsService.getClientVars().padTitle,
            slideCount: this.slideCountState.slideCount,
            releaseVersion: (window as any).__globalReleaseVersion,
            usedSsr: (window as any).previewTime ? 1 : 0,
        };
    }
}
